import { createStore, type Store } from "vuex";
import { useAuthStore } from "../auth/auth.store";
import { useUserStore } from "../user/user.store";

type LegacyStore = Store<Record<string, unknown>>;

let store: LegacyStore | null = null;

/**
 * The `hostStore` mount arg of the legacy AppMount contract: a Vuex 4 store
 * exposing the getter paths legacy remotes read from MerchantApp's store,
 * backed by the host's Pinia stores. No state of its own.
 *
 * Getter paths across the fleet (from the merchant-app-host POC audit):
 *   user/getUser, user/getLoggedInUser, user/getExternalIdInfo,
 *   user/getAbilities, featureFlags, pricing/userHomeCountry,
 *   enableCardProcessingChargesForNonUS, fulfillmentCenter/*.
 * Frozen: no new paths. New code reads host stores directly.
 */
export function createLegacyVuexStore(): LegacyStore {
  if (store) return store;

  store = createStore({
    getters: {
      featureFlags: () => ({}),
      enableCardProcessingChargesForNonUS: () => false,
    },
    modules: {
      user: {
        namespaced: true,
        getters: {
          getUser() {
            const userStore = useUserStore();
            const profile = (useAuthStore().userProfile as Record<string, unknown> | null) ?? {};
            const loggedInId = userStore.loggedInUser?.UserId ?? null;
            const impersonatedId = userStore.impersonatedUser?.UserId ?? null;
            // Composite shape covering every field path remotes read.
            return {
              id: loggedInId,
              impersonatedUserId:
                impersonatedId ?? (profile.shipbob_impersonating_user_id as string | null) ?? null,
              role: profile.shipbob_role ?? null,
              ImpersonatedUser: { UserId: impersonatedId },
              LoggedInUser: { UserId: loggedInId },
              User: { UserId: loggedInId },
              impersonatedUser: { userId: impersonatedId },
            };
          },
          getLoggedInUser: () => useUserStore().loggedInUser,
          getExternalIdInfo: () => useUserStore().externalIdInfo,
          getOneIdClaim: () => useUserStore().oneIdClaim,
          getAbilities: () => useAuthStore().ability ?? null,
        },
      },
      pricing: {
        namespaced: true,
        getters: { userHomeCountry: () => null },
      },
      fulfillmentCenter: {
        namespaced: true,
        state: () => ({ fulfillmentCenters: [] as unknown[] }),
        getters: {
          getFulfillmentCenters: (state: { fulfillmentCenters: unknown[] }) =>
            state.fulfillmentCenters,
        },
        mutations: {
          SET_FULFILLMENT_CENTERS(state: { fulfillmentCenters: unknown[] }, centers: unknown[]) {
            state.fulfillmentCenters = centers;
          },
        },
        actions: {
          async getFulfillmentCenters() {
            console.warn("[legacy vuexStore] fulfillmentCenter/getFulfillmentCenters is a stub");
          },
        },
      },
    },
  });

  return store;
}
